import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faDownload, 
  faShare, 
  faChevronLeft, 
  faChevronRight,
  faMusic,
  faVideo,
  faQuoteLeft,
  faCalendarAlt,
  faChartLine,
  faEnvelope,
  faPhone,
  faGlobe,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faApple, 
  faSoundcloud, 
  faYoutube, 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faTiktok,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import styles from './EPKViewer.module.css';

const EPKViewer = ({ isOpen, onClose, epkData, userName }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const epkContentRef = useRef(null);
  
  if (!isOpen || !epkData) return null;
  
  const {
    title,
    artistName,
    artistType,
    location,
    genres,
    activeSince,
    shortBio,
    longBio,
    photos,
    logo,
    music,
    videos,
    performances,
    stats,
    pressQuotes,
    pressCoverage,
    contact,
    socialLinks,
    theme
  } = epkData;
  
  // Format functions
  const formatLocation = () => {
    const parts = [];
    if (location?.city) parts.push(location.city);
    if (location?.state) parts.push(location.state);
    if (location?.country) parts.push(location.country);
    return parts.join(', ');
  };
  
  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  };
  
  // Handle image navigation
  const goToNextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % photos.length);
  };
  
  const goToPrevPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
  };
  
  const handleShareEPK = () => {
    const baseUrl = window.location.origin;
    let url;
    // Generate appropriate URL based on artist type
    if (userName) {
      if (epkData.artistType === 'Band') {
        url = `${baseUrl}/band/${userName}/epk`;
      } else {
        url = `${baseUrl}/${userName}/epk`;
      }
    } else {
      // Fallback URL if username is not available
      url = `${baseUrl}/${userName}/epk/${epkData.id || ''}`;
    }

    if (navigator.share) {
      navigator.share({
        title: `${artistName} - Electronic Press Kit`,
        text: shortBio || `Check out ${artistName}'s Electronic Press Kit`,
        url: url
      }).catch(err => {
        console.error('Error sharing:', err);
        navigator.clipboard.writeText(url);
        alert('EPK link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('EPK link copied to clipboard!');
    }
  };

  // Function to generate PDF version of the EPK
  const generatePDF = async () => {
    if (!epkContentRef.current) return;
    
    try {
      setIsPdfGenerating(true);
      
      // Get the theme colors from the EPK
      const computedStyle = window.getComputedStyle(document.querySelector(`.${styles.epkViewer}`));
      const accentColor = computedStyle.getPropertyValue('--accent-color').trim() || '#E2384D';
      const bgColor = computedStyle.getPropertyValue('background-color') || '#ffffff';
      
      // Clone the EPK content to modify for PDF
      const pdfContent = epkContentRef.current.cloneNode(true);
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(pdfContent);
      document.body.appendChild(tempDiv);
      
      // Apply EPK background color to the cloned content
      pdfContent.style.backgroundColor = bgColor;
      pdfContent.style.color = computedStyle.getPropertyValue('color');
      
      // Set temporary styles for PDF generation
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '794px'; // A4 width in pixels at 96 DPI (210mm)
      
      // Process iframe elements (videos, Spotify, etc.)
      const iframes = pdfContent.querySelectorAll('iframe');
      const iframePromises = Array.from(iframes).map(async (iframe, index) => {
        // Create a placeholder for the iframe with screenshot
        const placeholder = document.createElement('div');
        placeholder.style.position = 'relative';
        placeholder.style.width = iframe.width;
        placeholder.style.height = iframe.height;
        placeholder.style.backgroundColor = computedStyle.getPropertyValue('--epk-viewer-bg-secondary') || '#f8f8f8';
        placeholder.style.display = 'flex';
        placeholder.style.flexDirection = 'column';
        placeholder.style.alignItems = 'center';
        placeholder.style.justifyContent = 'center';
        placeholder.style.border = `1px solid ${computedStyle.getPropertyValue('--epk-viewer-border') || '#ddd'}`;
        placeholder.style.borderRadius = '8px';
        placeholder.style.padding = '20px';
        placeholder.style.boxSizing = 'border-box';
        
        // Create label based on iframe source
        let label, icon, linkText;
        const src = iframe.src.toLowerCase();
        
        if (src.includes('spotify')) {
          label = 'Spotify Track';
          icon = '🎵';
          linkText = 'Listen on Spotify';
        } else if (src.includes('apple')) {
          label = 'Apple Music Track';
          icon = '🎵';
          linkText = 'Listen on Apple Music';
        } else if (src.includes('soundcloud')) {
          label = 'SoundCloud Track';
          icon = '🎵';
          linkText = 'Listen on SoundCloud';
        } else if (src.includes('youtube')) {
          label = 'YouTube Video';
          icon = '📺';
          linkText = 'Watch on YouTube';
        } else {
          label = 'Media Content';
          icon = '📁';
          linkText = 'Access Media';
        }
        
        // Try to get a thumbnail for videos if possible
        if (src.includes('youtube.com/embed/')) {
          const videoId = src.split('/').pop().split('?')[0];
          const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
          
          // Create image element
          const img = document.createElement('img');
          img.src = thumbnailUrl;
          img.style.width = '100%';
          img.style.height = 'auto';
          img.style.borderRadius = '8px';
          img.style.marginBottom = '10px';
          placeholder.appendChild(img);
          
          // Add video title if available
          const titleElem = document.createElement('div');
          titleElem.textContent = iframe.title || 'Video';
          titleElem.style.fontWeight = 'bold';
          titleElem.style.marginBottom = '5px';
          placeholder.appendChild(titleElem);
        } else {
          // For non-YouTube media, show an icon and label
          const iconElem = document.createElement('div');
          iconElem.textContent = icon;
          iconElem.style.fontSize = '48px';
          iconElem.style.marginBottom = '10px';
          
          const labelElem = document.createElement('div');
          labelElem.textContent = label;
          labelElem.style.fontWeight = 'bold';
          labelElem.style.marginBottom = '10px';
          
          placeholder.appendChild(iconElem);
          placeholder.appendChild(labelElem);
        }
        
        // Add direct link to the media instead of QR code
        const linkContainer = document.createElement('div');
        linkContainer.style.marginTop = '10px';
        linkContainer.style.textAlign = 'center';
        
        const linkElem = document.createElement('a');
        linkElem.href = iframe.src;
        linkElem.textContent = linkText;
        linkElem.style.color = accentColor;
        linkElem.style.textDecoration = 'none';
        linkElem.style.fontWeight = 'bold';
        linkElem.style.padding = '8px 16px';
        linkElem.style.border = `2px solid ${accentColor}`;
        linkElem.style.borderRadius = '20px';
        linkElem.style.display = 'inline-block';
        
        const urlElem = document.createElement('div');
        urlElem.textContent = iframe.src;
        urlElem.style.fontSize = '10px';
        urlElem.style.marginTop = '8px';
        urlElem.style.wordBreak = 'break-all';
        urlElem.style.color = computedStyle.getPropertyValue('--epk-viewer-text-secondary') || '#666';
        
        linkContainer.appendChild(linkElem);
        linkContainer.appendChild(urlElem);
        placeholder.appendChild(linkContainer);
        
        // Replace iframe with the placeholder
        iframe.parentNode.replaceChild(placeholder, iframe);
      });
      
      // Wait for all iframe processing to complete
      await Promise.all(iframePromises);
      
      // Initialize PDF (A4 size) with the EPK background color
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Get dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Generate canvas from the prepared content
      const canvas = await html2canvas(pdfContent, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: bgColor // Match the EPK background color
      });
      
      // Calculate number of pages needed
      const contentHeight = canvas.height;
      const contentWidth = canvas.width;
      const ratio = contentWidth / contentHeight;
      const imgWidth = pdfWidth;
      const imgHeight = imgWidth / ratio;
      
      // Calculate total pages needed
      const totalPages = Math.ceil(contentHeight / (canvas.width * pdfHeight / pdfWidth));
      
      // Add each section to PDF
      let remainingHeight = contentHeight;
      let currentPosition = 0;
      
      // Convert theme accent color to RGB for PDF
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 226, g: 56, b: 77 }; // Default accent color
      };
      
      // Convert the accent color to RGB
      const accentRgb = hexToRgb(accentColor);
      
      // Convert background color to RGB if it's in hex format
      const getBgRgb = (color) => {
        // Check if it's a hex color
        if (color.startsWith('#')) {
          return hexToRgb(color);
        }
        // If it's rgb/rgba format
        const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
          return {
            r: parseInt(rgbMatch[1]),
            g: parseInt(rgbMatch[2]),
            b: parseInt(rgbMatch[3])
          };
        }
        return { r: 255, g: 255, b: 255 }; // Default white
      };
      
      const bgRgb = getBgRgb(bgColor);
      
      for (let i = 0; i < totalPages; i++) {
        // If not the first page, add a new page
        if (i > 0) {
          pdf.addPage();
        }
        
        // Set the page background color to match EPK
        pdf.setFillColor(bgRgb.r, bgRgb.g, bgRgb.b);
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
        
        // Calculate how much of the canvas to use for this page
        const canvasSliceHeight = (canvas.width * pdfHeight / pdfWidth);
        const sliceHeight = Math.min(canvasSliceHeight, remainingHeight);
        
        // Create a temporary canvas for the slice
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = contentWidth;
        tempCanvas.height = sliceHeight;
        const ctx = tempCanvas.getContext('2d');
        
        // Draw the slice of the original canvas to the temp canvas
        ctx.drawImage(
          canvas, 
          0, currentPosition, contentWidth, sliceHeight, 
          0, 0, contentWidth, sliceHeight
        );
        
        // Add to PDF
        const imgData = tempCanvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight * (sliceHeight / canvasSliceHeight));
        
        // Update for next slice
        currentPosition += sliceHeight;
        remainingHeight -= sliceHeight;
      }
      
      // Clean up
      document.body.removeChild(tempDiv);
      
      // Download the PDF
      pdf.save(`${artistName || 'Artist'}_EPK.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsPdfGenerating(false);
    }
  };
  
  // Determine theme classes based on selected theme
  const getThemeClasses = () => {
    const baseClass = styles.epkViewer;
    
    if (theme?.template === 'visual') {
      return `${baseClass} ${styles.visualTheme}`;
    } else if (theme?.template === 'classic') {
      return `${baseClass} ${styles.classicTheme}`;
    }
    
    return `${baseClass} ${styles.minimalTheme}`; // default
  };
  
  return (
    <div className={styles.epkViewerOverlay}>
      <div className={getThemeClasses()} style={{ 
        '--accent-color': theme?.accentColor || '#E2384D'
      }}>
        <div className={styles.epkHeader}>
          <h1>{title || `${artistName} - Electronic Press Kit`}</h1>
          
          <div className={styles.epkHeaderActions}>
            <button 
              className={styles.epkActionBtn}
              onClick={generatePDF}
              disabled={isPdfGenerating}
              title="Download PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </button>
            
            <button 
              className={styles.epkActionBtn}
              onClick={handleShareEPK}
              title="Share EPK"
            >
              <FontAwesomeIcon icon={faShare} />
            </button>
            
            <button 
              className={styles.epkActionBtn}
              onClick={onClose}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        <div className={styles.epkContent} ref={epkContentRef}>
          {isPdfGenerating && (
            <div className={styles.pdfGeneratingOverlay}>
              <div className={styles.pdfGeneratingContent}>
                <div className={styles.loadingSpinner}></div>
                <p>Generating PDF...</p>
              </div>
            </div>
          )}
          {/* Hero Section */}
          <section className={styles.heroSection}>
            {logo && (
              <div className={styles.logoContainer}>
                <img src={logo.url} alt={`${artistName} logo`} className={styles.artistLogo} />
              </div>
            )}
            
            <h2 className={styles.artistName}>{artistName}</h2>
            {artistType && <h3 className={styles.artistType}>{artistType}</h3>}
            
            <div className={styles.artistDetails}>
              {formatLocation() && (
                <div className={styles.artistLocation}>
                  {formatLocation()}
                </div>
              )}
              
              {genres.length > 0 && (
                <div className={styles.artistGenres}>
                  {genres.join(' • ')}
                </div>
              )}
              
              {activeSince && (
                <div className={styles.artistSince}>
                  Est. {activeSince}
                </div>
              )}
            </div>
            
            {shortBio && (
              <div className={styles.artistShortBio}>
                {shortBio}
              </div>
            )}
            
            {/* Social Media Links */}
            <div className={styles.socialLinks}>
              {socialLinks?.spotify && (
                <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faSpotify} />
                </a>
              )}
              
              {socialLinks?.appleMusic && (
                <a href={socialLinks.appleMusic} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faApple} />
                </a>
              )}
              
              {socialLinks?.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
              )}
              
              {socialLinks?.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
              )}
              
              {socialLinks?.twitter && (
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faTwitter} />
                </a>
              )}
              
              {socialLinks?.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
              )}
              
              {socialLinks?.youtube && (
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faYoutube} />
                </a>
              )}
              
              {socialLinks?.soundcloud && (
                <a href={socialLinks.soundcloud} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faSoundcloud} />
                </a>
              )}
              
              {socialLinks?.linkedin && (
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faLinkedin} />
                </a>
              )}
            </div>
          </section>
          
          {/* Photos Section */}
          {photos && photos.length > 0 && (
            <section className={styles.photosSection}>
              <h3 className={styles.sectionTitle}>
                Photos
              </h3>
              
              <div className={styles.photoSlider}>
                <div className={styles.mainPhoto}>
                  <img 
                    src={photos[currentPhotoIndex].url} 
                    alt={`${artistName} promo ${currentPhotoIndex + 1}`} 
                  />
                  
                  {photos.length > 1 && (
                    <>
                      <button 
                        className={`${styles.photoNavBtn} ${styles.photoNavBtnPrev}`}
                        onClick={goToPrevPhoto}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      
                      <button 
                        className={`${styles.photoNavBtn} ${styles.photoNavBtnNext}`}
                        onClick={goToNextPhoto}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                      
                      <div className={styles.photoCounter}>
                        {currentPhotoIndex + 1} / {photos.length}
                      </div>
                    </>
                  )}
                </div>
                
                {photos.length > 1 && (
                  <div className={styles.photoThumbnails}>
                    {photos.map((photo, index) => (
                      <div 
                        key={photo.id || index}
                        className={`${styles.photoThumb} ${index === currentPhotoIndex ? styles.activeThumb : ''}`}
                        onClick={() => setCurrentPhotoIndex(index)}
                      >
                        <img src={photo.thumbnailUrl || photo.url} alt={`Thumbnail ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}
          
          {/* Bio Section */}
          {longBio && (
            <section className={styles.bioSection}>
              <h3 className={styles.sectionTitle}>Biography</h3>
              <div className={styles.bioContent}>
                {longBio}
              </div>
            </section>
          )}
          
          {/* Music Section */}
          {((music?.spotify && music.spotify.length > 0) || 
            (music?.appleMusic && music.appleMusic.length > 0) || 
            (music?.soundcloud && music.soundcloud.length > 0)) && (
            <section className={styles.musicSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faMusic} />
                Music
              </h3>
              
              <div className={styles.musicEmbeds}>
                {music?.spotify && music.spotify.map((track, index) => (
                  <div key={`spotify-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      src={`https://open.spotify.com/embed/track/${track.id}`}
                      width="100%" 
                      height="80" 
                      frameBorder="0" 
                      allowtransparency="true" 
                      allow="encrypted-media">
                    </iframe>
                  </div>
                ))}
                
                {music?.appleMusic && music.appleMusic.map((track, index) => (
                  <div key={`apple-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      allow="autoplay *; encrypted-media *; fullscreen *" 
                      frameBorder="0" 
                      height="150" 
                      width="100%" 
                      sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" 
                      src={track.embedUrl}>
                    </iframe>
                  </div>
                ))}
                
                {music?.soundcloud && music.soundcloud.map((track, index) => (
                  <div key={`soundcloud-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      width="100%" 
                      height="166" 
                      scrolling="no" 
                      frameBorder="no" 
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.url)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}>
                    </iframe>
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Videos Section */}
          {videos && videos.length > 0 && (
            <section className={styles.videoSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faVideo} />
                Videos
              </h3>
              
              <div className={styles.videoGrid}>
                {videos.map((video, index) => (
                  <div key={`video-${index}`} className={styles.videoEmbed}>
                    <iframe 
                      width="100%" 
                      height="315" 
                      src={video.embedUrl} 
                      title={video.title || `Video ${index + 1}`}
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen>
                    </iframe>
                    
                    {video.title && (
                      <h4 className={styles.videoTitle}>{video.title}</h4>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Performances Section */}
          {performances && performances.length > 0 && (
            <section className={styles.performancesSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                Performance History
              </h3>
              
              <div className={styles.performancesGrid}>
                {performances.map((performance, index) => (
                  <div key={`perf-${index}`} className={styles.performanceCard}>
                    <h4 className={styles.perfTitle}>{performance.name}</h4>
                    
                    <div className={styles.perfDetails}>
                      <span className={styles.perfYear}>{performance.year}</span>
                      {performance.city && (
                        <span className={styles.perfCity}>{performance.city}</span>
                      )}
                    </div>
                    
                    {performance.isHeadliner && (
                      <div className={styles.perfHeadliner}>Headliner</div>
                    )}
                    
                    {performance.audienceSize && (
                      <div className={styles.perfAudience}>
                        Audience: {formatNumber(performance.audienceSize)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Stats Section */}
          {stats && (
            <section className={styles.statsSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faChartLine} />
                Stats
              </h3>
              
              <div className={styles.statsGrid}>
                {stats.spotify?.monthlyListeners && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faSpotify} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(stats.spotify.monthlyListeners)}
                    </div>
                    <div className={styles.statLabel}>
                      Monthly Listeners
                    </div>
                  </div>
                )}
                
                {stats.spotify?.totalStreams && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faSpotify} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(stats.spotify.totalStreams)}
                    </div>
                    <div className={styles.statLabel}>
                      Total Streams
                    </div>
                  </div>
                )}
                
                {stats.youtube?.totalViews && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faYoutube} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(stats.youtube.totalViews)}
                    </div>
                    <div className={styles.statLabel}>
                      YouTube Views
                    </div>
                  </div>
                )}
                
                {stats.instagram && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faInstagram} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(stats.instagram)}
                    </div>
                    <div className={styles.statLabel}>
                      Instagram Followers
                    </div>
                  </div>
                )}
                
                {stats.tiktok && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faTiktok} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(stats.tiktok)}
                    </div>
                    <div className={styles.statLabel}>
                      TikTok Followers
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
          
          {/* Press Quotes Section */}
          {pressQuotes && pressQuotes.length > 0 && (
            <section className={styles.pressSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faQuoteLeft} />
                Press
              </h3>
              
              <div className={styles.pressQuotes}>
                {pressQuotes.map((quote, index) => (
                  <div key={`quote-${index}`} className={styles.quoteCard}>
                    <div className={styles.quoteText}>
                      "{quote.quote}"
                    </div>
                    <div className={styles.quoteSource}>
                      — {quote.source}
                      {quote.link && (
                        <a 
                          href={quote.link.startsWith('http') ? quote.link : `https://${quote.link.replace(/^\/+/, '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.quoteLink}
                        >
                          Read More
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {pressCoverage && pressCoverage.length > 0 && (
                <div className={styles.pressCoverage}>
                  <h4 className={styles.pressCoverageTitle}>Media Coverage</h4>
                  <ul className={styles.pressCoverageList}>
                    {pressCoverage.map((coverage, index) => (
                      <li key={`coverage-${index}`} className={styles.pressCoverageItem}>
                        <a 
                          href={coverage.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.coverageLink}
                        >
                          {coverage.title} — {coverage.publication}
                          {coverage.date && ` (${coverage.date})`}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
          
          {/* Contact Section */}
          {contact && (
            <section className={styles.contactSection}>
              <h3 className={styles.sectionTitle}>
                Contact
              </h3>
              
              <div className={styles.contactGrid}>
                {contact.name && contact.role && (
                  <div className={styles.contactPerson}>
                    <div className={styles.contactName}>{contact.name}</div>
                    <div className={styles.contactRole}>{contact.role}</div>
                  </div>
                )}
                
                <div className={styles.contactInfo}>
                  {contact.email && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faEnvelope} className={styles.contactIcon} />
                      <a href={`mailto:${contact.email}`} className={styles.contactLink}>
                        {contact.email}
                      </a>
                    </div>
                  )}
                  
                  {contact.phone && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faPhone} className={styles.contactIcon} />
                      <a href={`tel:${contact.phone}`} className={styles.contactLink}>
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  
                  {contact.website && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faGlobe} className={styles.contactIcon} />
                      <a 
                        href={contact.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.contactLink}
                      >
                        {contact.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
        
        <div className={styles.epkFooter}>
          <div className={styles.footerContent}>
            Generated with Trillio EPK Builder
            <div className={styles.epkTimestamp}>
              Last updated: {new Date(epkData.updatedAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EPKViewer;